/**
 * bridge/print_controller.js — "print" bridge component (Stimulus + Strada).
 *
 * Hands the CURRENT page to the OS print system. Stimulus identifier
 * "bridge--print" (glob-derived from this subdir); Strada component name "print"
 * MUST match the iOS class var name (native/ios/Chippie/Bridge/PrintComponent.swift).
 *
 * Why native rather than window.print(): inside a WKWebView, window.print() does
 * nothing at all — iOS simply has no print handler for it. Android WebView is no
 * better. So on both shells the print button on a quote or invoice is silently
 * dead unless it goes through this component. The native sheets also reach
 * things the web cannot: AirPrint, "Save to Files", the share-to-PDF path, and
 * Bluetooth thermal printers a tradie has paired for on-site receipts.
 *
 * The contract is deliberately no-URL: every platform prints the page currently
 * rendered. Accepting an arbitrary URL would mean iOS fetches and renders it
 * off-screen while Android can only print what is loaded — a contract only one
 * platform could honour. Callers navigate to the print-preview page first (the
 * existing print_preview.html flow already works this way) and then call print().
 *
 * API — window.ChippieBridge.print(jobName) -> Boolean (did a sheet open):
 *   jobName  optional label for the OS print job / saved PDF, e.g. "Invoice 1042".
 *            Defaults to the document title.
 *
 * Three-way transport (facade SSOT, chippie-bridge.js owns platform detection):
 *   - ios:     this.send("print", { jobName }) -> UIPrintInteractionController
 *              over the WKWebView's viewPrintFormatter().
 *   - android: window.ChippieNative.printPage(jobName) -> PrintManager over
 *              webView.createPrintDocumentAdapter().
 *   - pwa:     window.print(). Works in a real browser (which is the only place
 *              it does work).
 *
 * NOT server-drivable: the print sheet is a full-screen system takeover. It is
 * absent from REMOTE_ALLOWED on purpose — a print dialog appearing with no user
 * gesture behind it is indistinguishable from a bug.
 *
 * Template wiring (one global instance in the shell is enough):
 *   <div data-controller="bridge--print"></div>
 * Declarative trigger, no JS:
 *   <button data-controller="bridge--invoke"
 *           data-bridge-call="print"
 *           data-bridge-args='["Invoice 1042"]'>Print</button>
 *
 * Outside a native shell BridgeComponent never connect()s, so the Android/PWA
 * impls are wired standalone at module load and connect() upgrades iOS.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

function jobLabel(jobName) {
    return jobName || document.title || "Chippie";
}

function androidPrint(jobName) {
    if (!window.ChippieNative || !window.ChippieNative.printPage) return false;
    try {
        window.ChippieNative.printPage(jobLabel(jobName));
        return true;
    } catch (_e) {
        return false;
    }
}

function pwaPrint() {
    try {
        window.print();
        return true;
    } catch (_e) {
        return false;
    }
}

function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("print", {
        // ios is overridden by connect() with a real send. window.print() is a
        // no-op in WKWebView, so the placeholder reports failure rather than
        // pretending a sheet opened.
        ios: () => false,
        android: androidPrint,
        pwa: pwaPrint,
    });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "print";

    connect() {
        super.connect();
        if (window.ChippieBridge && window.ChippieBridge.register) {
            window.ChippieBridge.register("print", {
                ios: (jobName) => {
                    this.send("print", { jobName: jobLabel(jobName) });
                    return true;
                },
                android: androidPrint,
                pwa: pwaPrint,
            });
        }
    }

    disconnect() {
        registerStandalone();
        super.disconnect();
    }
}
